import { useEffect, useRef } from "react";
import { useStore } from "react-redux";
import { useTopFolderZipStatusBatchMutation } from "features/fileSystem/filesStorage";
import {
  removeTopFolderZipJob,
  setTopFolderZipReady,
} from "features/topFolderZipSlice";
import { pushNotification } from "features/notificationSlice";
import { useAppDispatch, useAppSelector } from "features/store";
import type { RootState } from "features/store";

export default function TopFolderZipJobPoller() {
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;

  const [postStatusBatch] = useTopFolderZipStatusBatchMutation();

  const jobCount = useAppSelector((s) => Object.keys(s.topFolderZip.jobs).length);

  useEffect(() => {
    if (jobCount === 0) return;
    let stopped = false;
    let tickInFlight = false;
    const runTick = async () => {
      if (stopped || tickInFlight) return;
      tickInFlight = true;
      try {
        const jobs = store.getState().topFolderZip.jobs;
        const keys = Object.keys(jobs);
        const itemKeysByJobId = new Map<string, string[]>();
        for (const itemKey of keys) {
          const jid = jobs[itemKey]?.jobId;
          if (!jid) continue;
          const arr = itemKeysByJobId.get(jid) ?? [];
          arr.push(itemKey);
          itemKeysByJobId.set(jid, arr);
        }
        const uniqueJobIds = Array.from(itemKeysByJobId.keys());
        if (uniqueJobIds.length === 0) return;

        try {
          const MAX_BATCH = 64;
          const chunks: string[][] = [];
          for (let i = 0; i < uniqueJobIds.length; i += MAX_BATCH) {
            chunks.push(uniqueJobIds.slice(i, i + MAX_BATCH));
          }

          const results = (
            await Promise.all(
              chunks.map((ids) =>
                postStatusBatch({ job_ids: ids }).unwrap(),
              ),
            )
          ).flatMap((r) => r.results);

          for (const row of results) {
            const itemKeys = itemKeysByJobId.get(row.job_id) ?? [];
            if ("error" in row) {
              for (const itemKey of itemKeys) {
                dispatchRef.current(removeTopFolderZipJob({ itemKey }));
                dispatchRef.current(
                  pushNotification({
                    message: row.error,
                    severity: "error",
                  }),
                );
              }
              continue;
            }
            if (row.status === "completed") {
              for (const itemKey of itemKeys) {
                const job = jobs[itemKey];
                if (!job?.jobId) continue;
                dispatchRef.current(
                  setTopFolderZipReady({
                    itemKey,
                    jobId: job.jobId,
                    label: job.label,
                  }),
                );
                dispatchRef.current(removeTopFolderZipJob({ itemKey }));
                dispatchRef.current(
                  pushNotification({
                    id: `tfz-ready-${itemKey}`,
                    message: `ZIP "${job.label}" is ready to download.`,
                    severity: "success",
                    autoHideMs: null,
                    downloadAction: {
                      kind: "top_folder_zip_download",
                      jobId: job.jobId,
                      itemKey,
                      label: job.label,
                    },
                  }),
                );
              }
            } else if (row.status === "failed") {
              for (const itemKey of itemKeys) {
                const job = jobs[itemKey];
                dispatchRef.current(removeTopFolderZipJob({ itemKey }));
                dispatchRef.current(
                  pushNotification({
                    message:
                      row.error_message?.trim() ||
                      `ZIP build failed for "${job?.label ?? "folder"}".`,
                    severity: "error",
                  }),
                );
              }
            }
          }
        } catch {
        }
      } finally {
        tickInFlight = false;
      }
    };

    void runTick();
    const interval = setInterval(() => void runTick(), 2000);

    return () => {
      stopped = true;
      clearInterval(interval);
    };
  }, [jobCount, store, postStatusBatch]);

  return null;
}
